/*
 * ClaimPlatformGate — first-boot one-time modal that shows when the
 * seed admin (admin@traqo.com) is logged in AND no real admin has
 * claimed the platform yet. Forces creation of a real owner account
 * and demotes the seed.
 */
import { useEffect, useState } from "react";
import { Crown, Shield, Loader2, ArrowRight, Sparkles } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";


export default function ClaimPlatformGate() {
  const [shouldShow, setShouldShow] = useState(false);
  const [skipped, setSkipped] = useState(() => sessionStorage.getItem("claim_skipped") === "1");
  const [form, setForm] = useState({ email: "", name: "", password: "", recovery_email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/admin/bootstrap-status")
       .then(r => setShouldShow(r.data.should_show_claim_modal))
       .catch(() => {/* not admin or auth not ready */});
  }, []);

  if (!shouldShow || skipped || done) return null;

  const submit = async () => {
    if (form.password.length < 8) { toast.error("Password must be 8+ characters"); return; }
    if (!form.email.includes("@")) { toast.error("Valid email required"); return; }
    if (form.name.trim().length < 2) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/claim-platform", form);
      setDone(true);
      toast.success(`Platform claimed by ${r.data.new_email}! Logging out the seed admin in 4s…`, { duration: 4000 });
      setTimeout(() => {
        localStorage.removeItem("token");
        window.location.href = "/admin/login";
      }, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={shouldShow && !skipped && !done}>
      <DialogContent className="max-w-lg" data-testid="claim-platform-modal" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-600"/>
            Claim this platform
          </DialogTitle>
          <DialogDescription>
            You are signed in as the <strong>seed admin</strong> (admin@traqo.com). Before going live, create your real owner account.
            The seed admin will be demoted + suspended so it can't be used to bypass your security.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm py-2">
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 flex items-start gap-2">
            <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"/>
            <span>The new account auto-promotes to <strong>tier_unlimited</strong> · full panic-button + impersonation + override powers · zero quota gate.</span>
          </div>
          <div>
            <Label>Your real email <span className="text-rose-600">*</span></Label>
            <Input type="email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@yourcompany.com" data-testid="claim-email" autoFocus/>
          </div>
          <div>
            <Label>Your name <span className="text-rose-600">*</span></Label>
            <Input value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Jane Doe" data-testid="claim-name"/>
          </div>
          <div>
            <Label>Password (min 8 chars) <span className="text-rose-600">*</span></Label>
            <Input type="password" value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Strong password" data-testid="claim-password"/>
          </div>
          <div>
            <Label>Recovery email (optional)</Label>
            <Input type="email" value={form.recovery_email}
                    onChange={(e) => setForm({ ...form, recovery_email: e.target.value })}
                    placeholder="personal@email.com" data-testid="claim-recovery"/>
          </div>
        </div>
        <div className="flex items-center justify-between pt-2 border-t">
          <button onClick={() => { sessionStorage.setItem("claim_skipped", "1"); setSkipped(true); }}
                  className="text-[11px] text-foreground/55 hover:text-foreground"
                  data-testid="claim-skip">
            Skip for now
          </button>
          <Button onClick={submit} disabled={busy || form.password.length < 8 || !form.email || form.name.trim().length < 2}
                  data-testid="claim-submit">
            {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/>}
            <Shield className="w-3.5 h-3.5 mr-1"/>Claim platform
            <ArrowRight className="w-3.5 h-3.5 ml-1"/>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
